<template>
  <TModal
    v-model:visible="visible"
    :has-priority="shouldTrapFocus"
  >
    <AtomTransition
      v-if="size !== 'full'"
      appear
    >
      <TModalBackground
        v-if="isVisible && !isAnimating"
      />
    </AtomTransition>

    <div
      ref="modalRef"
      class="h-full"
    >
      <TModalContentsWrapper :class="wrapperClasses">
        <AtomTransition
          appear
          :variant="contentTransitionVariant"
          @after-leave="handleAfterLeave"
          @after-enter="emit('visible:content')"
        >
          <TModalContents
            v-if="isVisible && !isAnimating"
            :class="contentClasses"
          >
            <AtomTransition appear>
              <TOverlay
                :show="isLoading"
                position="absolute"
               
                blur
                :rounded="loaderRounded"
              />
            </AtomTransition>

            <TModalHeader
              enable-close-button
              :title="title"
              :subtitle="subtitle"
              :class="headerClasses"
              @close="closeWithExitButton"
            >
              <template #icon>
                <slot name="icon" />
              </template>
            </TModalHeader>

            <TModalBody
              :class="bodyClasses"
              :enable-scroll="enableScroll"
            >
              <slot :dismiss="closeWithButton" />
            </TModalBody>

            <TModalFooter
              v-if="hasFooter"
              :class="footerClasses"
              :label-primary="labelPrimary"
              :label-secondary="labelSecondary"
              :label-tertiary="labelTertiary"
              :variant="variant"
              @action:primary="() => emit('action:primary', closeWithButton)"
              @action:secondary="() => emit('action:secondary', closeWithButton)"
              @action:tertiary="() => emit('action:tertiary', closeWithButton)"
            >
              <template #button-primary>
                <slot
                  name="button-primary"
                  :dismiss="closeWithButton"
                />
              </template>

              <template #button-secondary>
                <slot
                  name="button-secondary"
                  :dismiss="closeWithButton"
                />
              </template>

              <template #button-tertiary>
                <slot
                  name="button-tertiary"
                  :dismiss="closeWithButton"
                />
              </template>
            </TModalFooter>
          </TModalContents>
        </AtomTransition>
      </TModalContentsWrapper>
    </div>
  </TModal>
</template>

<script setup lang="ts">
import { useModal } from './useModal'
import type { ModalCloseTrigger, ModalDrawerPosition, ModalSize, ModalVariant } from './types'

/**
 * This modal variation is used for when we want the user to either remain in
 * the current flow, or prevent them from accidentally closing the modal with a
 * background click or escape key.
 *
 * Good examples are the login modal and modals that show up with new/critical
 * information.
 */
const props = withDefaults(defineProps<{
  title: string
  subtitle?: string
  size?: ModalSize
  positionDrawer?: ModalDrawerPosition
  labelPrimary?: string
  labelSecondary?: string
  labelTertiary?: string
  variant?: ModalVariant
  enableScroll?: boolean
  isLoading?: boolean
}>(), {
  variant: 'default',
  size: 'md',
  positionDrawer: 'bottom',
})

const emit = defineEmits<{
  'close': [trigger: ModalCloseTrigger]
  'visible:content': []
  'action:primary': [dismiss: () => void]
  'action:secondary': [dismiss: () => void]
  'action:tertiary': [dismiss: () => void]
}>()

const visible = defineModel<boolean>('visible', { default: false })

const { isSmBreakpoint } = useBreakpoint()

const loaderRounded = computed(() => {
  if (isSmBreakpoint.value) {
    return true
  }

  return props.positionDrawer === 'bottom' ? 'top' : 'bottom'
})

const modalRef = useTemplateRef<HTMLDivElement>('modalRef')

const {
  wrapperClasses,
  contentClasses,
  headerClasses,
  bodyClasses,
  footerClasses,
  contentTransitionVariant,

  isVisible,
  isAnimating,

  handleAfterLeave,
  closeWithButton,
  closeWithExitButton,
  shouldTrapFocus,
} = useModal({
  enableScrollLock: true,
  size: toRef(props, 'size'),
  positionDrawer: props.positionDrawer,
  visible,
  modalRef,
  focusTrapOptions: { escapeDeactivates: false },
  onClose: (trigger) => {
    emit('close', trigger)
  },
})

const slots = useSlots()

const hasFooter = computed(() => {
  return props.labelPrimary || props.labelSecondary || props.labelTertiary || slots['button-primary'] || slots['button-secondary'] || slots['button-tertiary']
})
</script>
